import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { claimKey } from './claimFormatters';

const PAGE_LIMIT = 50;

// One instance per tab (active/completed/canceled). Mirrors
// RiderDriverExclusionsShell's per-stream state: a generation ref bumped on
// every refetch so a stale in-flight request (a superseded "Load more", or a
// page-1 fetch from a query that has since changed) can't clobber a newer
// snapshot, plus a synchronous in-flight guard so two rapid "Load more"
// clicks in the same tick don't both capture the same cursor.
const useClaimsTab = (status, { searchQuery, questId } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  // Tracked separately from `error` (a refetch failure) so the banner can
  // tell "the page-1 fetch for this query failed" apart from "a Load more
  // page append failed" — the two need different copy and the latter should
  // never be reported as "couldn't refresh".
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [cursor, setCursor] = useState(undefined);
  const [exhausted, setExhausted] = useState(false);

  const generationRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);

  const refetch = useCallback(async () => {
    const generation = ++generationRef.current;
    // Any "Load more" already in flight belongs to the generation this
    // refetch just superseded. Its result will be dropped by the generation
    // check below regardless, but releasing the guard now (rather than
    // waiting for that stale request to resolve) means the first "Load more"
    // click against the *new* page 1 isn't silently swallowed. Its `finally`
    // is itself gated on the generation (see below), so it won't release
    // these on its own — reset them here instead.
    loadMoreInFlightRef.current = false;
    setLoadingMore(false);
    // A new query/filter starts clean: any error from a Load more against the
    // *previous* query no longer applies once that query's rows are replaced.
    setLoadMoreError(false);
    setLoading(true);
    try {
      const result = await api.driverQuestClaims.getClaims({ status, searchQuery, questId, limit: PAGE_LIMIT });
      if (generationRef.current !== generation) return;
      setRows((result && result.entries) || []);
      setCursor(result ? result.nextCursor || null : null);
      setExhausted(!(result && result.nextCursor));
      // A failed load must not masquerade as an empty list — clear the error
      // only on success, and set it in the catch so the tab shows "couldn't
      // load" instead of the (misleading) empty state.
      setError(false);
    } catch (err) {
      if (generationRef.current !== generation) return;
      // Keep the previously loaded rows (so the tab shows "couldn't refresh —
      // showing the last loaded results" rather than going blank), but the
      // cursor on those rows belongs to the *old* query. Mark this query
      // exhausted so "Load more" is hidden instead of appending rows for the
      // new search/filter under the old query's cursor position.
      setExhausted(true);
      setError(true);
    } finally {
      // Gated on the generation: an unconditional reset here would flip
      // `loading` to false for a *newer*, still-pending refetch when this
      // superseded one resolves (e.g. a slow page-1 fetch for a search query
      // that's since changed again), which both misreports progress and —
      // combined with `loadMore`'s own `loading` guard below — would let a
      // "Load more" click through against a cursor that isn't this query's.
      if (generationRef.current === generation) {
        setLoading(false);
      }
    }
  }, [status, searchQuery, questId]);

  const loadMore = useCallback(async () => {
    // Bail while a refetch is in flight (new query/filter, or the initial
    // mount): its result will replace `rows`/`cursor` out from under this
    // call anyway, and firing now would pair the *new* generation (so it
    // survives the generation check) with the *old* query's cursor, which is
    // how a stale page got appended to a freshly-filtered tab.
    if (loading || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;
    const generation = generationRef.current;
    setLoadingMore(true);
    try {
      const result = await api.driverQuestClaims.getClaims({
        status,
        searchQuery,
        questId,
        cursor,
        limit: PAGE_LIMIT,
      });
      if (generationRef.current !== generation) return;
      const entries = (result && result.entries) || [];
      // Dedupe on append is defensive only — the server cursor should already
      // prevent overlap — but it's what keeps a 650-row, 13-page pull free of
      // duplicates if a page is ever re-delivered.
      setRows((prev) => {
        const seen = new Set(prev.map(claimKey));
        return [...prev, ...entries.filter((e) => !seen.has(claimKey(e)))];
      });
      setCursor(result ? result.nextCursor || null : cursor);
      setExhausted(!(result && result.nextCursor));
      setLoadMoreError(false);
    } catch (err) {
      if (generationRef.current !== generation) return;
      // Keep the loaded rows + cursor so "Load more" stays available to retry.
      // This is a page-append failure, not a refetch failure — tracked on
      // `loadMoreError`, not `error`, so the banner doesn't call it a
      // "refresh" when nothing was refreshed.
      setLoadMoreError(true);
    } finally {
      // Gated on the generation: a stale "Load more" (superseded by a refetch
      // that's already resolved and started its own "Load more") resolving
      // late must not clear the guard/`loadingMore` out from under that
      // newer, still-in-flight call — doing so would let a third click fire
      // concurrently with it. `refetch` already releases the guard
      // synchronously for the case where no newer "Load more" is in flight.
      if (generationRef.current === generation) {
        loadMoreInFlightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [status, searchQuery, questId, cursor, loading]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { rows, loading, loadingMore, error, loadMoreError, hasMore: !exhausted, loadMore };
};

export default useClaimsTab;
